import {faUpload} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Alert, Button} from 'antd';
import {RcFile} from 'antd/lib/upload';
import {useCallback, useEffect, useMemo, useState} from 'react';

import {AdditionalInfo} from '../../../../../../layout/additional-info/additional-info';
import {Locale} from '../../../../../../provider/locale/locale';
import {useLocale} from '../../../../../../provider/locale/locale-hook';
import {CustomFileType, UploadFileResponseType} from '../../../../../../service/file-upload/file-upload-type';
import {useFormRules} from '../../../../../../service/form/form-rules-hook';
import {useUploadStoryImage} from '../../../../../../service/yandex-stories/yandex-stories-hook';
import {
    YandexStoryFormFieldsEnum,
    YandexStoryFormType,
} from '../../../../../../service/yandex-stories/yandex-stories-type';
import {Form, FormInstance} from '../../../../../../typings/antd';
import {noop} from '../../../../../../util/function';
import {useRefreshId} from '../../../../../../util/hook';
import {FileFormInput} from '../../../../../form/file-upload-item/file-form-input';
import {Text} from '../../../../../text/text';
import {
    YANDEX_STORY_MAX_IMAGE_HEIGHT,
    YANDEX_STORY_MAX_IMAGE_WIDTH,
    YANDEX_STORY_MIN_IMAGE_HEIGHT,
    YANDEX_STORY_MIN_IMAGE_WIDTH,
} from '../../../yandex-story-form-helper';

import * as styles from './yandex-story-cover-field.scss';

// Matches the slides uploader, and the size the API accepts.
const YANDEX_STORY_COVER_MAX_FILE_SIZE = 10;

type CoverUploadMapType = Record<string, CustomFileType & UploadFileResponseType>;

type PropsType = {
    formInstance: FormInstance<YandexStoryFormType>;
};

// The cover is a property of the story, not of a slide: the catalog uploads it as an image of its own
// (`stories/catalogs/yandex.py`), so it does not have to be one of the slides. By default it is the
// first slide, the preview's own button picks another one, and this field uploads a file instead.
export function YandexStoryCoverField(props: PropsType): JSX.Element {
    const {formInstance} = props;

    const {getLocalizedString} = useLocale();
    const {validateImageDimension} = useFormRules();
    const {mutateAsync: uploadFile} = useUploadStoryImage();

    const [uploadMap, setUploadMap] = useState<CoverUploadMapType>({});
    const [hasUploadError, setHasUploadError] = useState<boolean>(false);

    // Remounts the input once an upload has been handled, so the next one starts from an empty list.
    const {refresh, refreshId} = useRefreshId();

    const cover = Form.useWatch([YandexStoryFormFieldsEnum.Cover], {form: formInstance, preserve: true});
    const slides = Form.useWatch([YandexStoryFormFieldsEnum.Slides], {form: formInstance, preserve: true});

    const {isCoverFromSlides, coverUrl} = useMemo(() => {
        const slidesByOrder = Object.values(slides ?? {}).sort((slide1, slide2) => slide1.order - slide2.order);
        // No explicit pick means the first slide, exactly as the create request resolves it.
        const coverSlide = cover ? slidesByOrder.find((slide) => slide.id === cover.id) : slidesByOrder[0];

        return {
            isCoverFromSlides: Boolean(coverSlide),
            coverUrl: cover?.url || slidesByOrder[0]?.url || '',
        };
    }, [cover, slides]);

    useEffect(() => {
        const uploadedFiles = Object.values(uploadMap);

        if (uploadedFiles.length === 0 || uploadedFiles.some((file) => file.status === 'uploading')) {
            return;
        }

        const uploadedCover = uploadedFiles.find((file) => file.status === 'done');

        if (uploadedCover) {
            formInstance.setFieldValue([YandexStoryFormFieldsEnum.Cover], {
                id: uploadedCover.id,
                url: uploadedCover.url,
            });
        }

        setHasUploadError(!uploadedCover);
        setUploadMap({});
        refresh();
    }, [formInstance, refresh, uploadMap]);

    // Back to the default: the first slide becomes the cover again, as it is on a fresh story.
    const handleReset = useCallback(() => {
        setHasUploadError(false);
        formInstance.setFieldValue([YandexStoryFormFieldsEnum.Cover], null);
    }, [formInstance]);

    const isUploading = Object.values(uploadMap).some((file) => file.status === 'uploading');

    return (
        <div className={styles.YandexStoryCoverField}>
            <AdditionalInfo leftNode={<Locale stringKey="YANDEX_STORY_FORM__COVER__LABEL" />}>
                <Text block bolder stringKey="YANDEX_STORY_FORM__COVER__RULES_TITLE" />

                <ul className={styles.YandexStoryCoverField_rules}>
                    <li>
                        <Locale stringKey="YANDEX_STORY_FORM__UPLOAD_IMAGES__RULES_2__SHORT" />
                    </li>
                    <li>
                        <Locale stringKey="YANDEX_STORY_FORM__UPLOAD_IMAGES__RULES_3__SHORT" />
                    </li>
                    <li>
                        <Locale stringKey="YANDEX_STORY_FORM__COVER__RULES_ASPECT_RATIO" />
                    </li>
                </ul>
            </AdditionalInfo>

            <div className={styles.YandexStoryCoverField_body}>
                <div className={styles.YandexStoryCoverField_preview} style={{backgroundImage: `url("${coverUrl}")`}} />

                <div className={styles.YandexStoryCoverField_info}>
                    <Text
                        block
                        secondary
                        small
                        stringKey={
                            isCoverFromSlides
                                ? 'YANDEX_STORY_FORM__COVER__FROM_SLIDE'
                                : 'YANDEX_STORY_FORM__COVER__CUSTOM'
                        }
                    />

                    <div className={styles.YandexStoryCoverField_actions}>
                        <FileFormInput
                            key={refreshId}
                            onChange={setUploadMap}
                            remove={noop}
                            showCustomUploadList={false}
                            upload={uploadFile}
                            uploadProps={{
                                accept: '.png,.jpg',
                                maxCount: 1,
                                maxFileSize: YANDEX_STORY_COVER_MAX_FILE_SIZE,
                            }}
                            validationRules={[
                                async (file: RcFile) =>
                                    validateImageDimension(file, {
                                        min: {
                                            height: YANDEX_STORY_MIN_IMAGE_HEIGHT,
                                            width: YANDEX_STORY_MIN_IMAGE_WIDTH,
                                        },
                                        max: {
                                            height: YANDEX_STORY_MAX_IMAGE_HEIGHT,
                                            width: YANDEX_STORY_MAX_IMAGE_WIDTH,
                                        },
                                    }),
                            ]}
                            value={uploadMap}
                        >
                            <Button
                                icon={<FontAwesomeIcon icon={faUpload} />}
                                loading={isUploading}
                                size="small"
                                type="link"
                            >
                                <Locale stringKey="YANDEX_STORY_FORM__COVER__UPLOAD_ANOTHER" />
                            </Button>
                        </FileFormInput>

                        {!isCoverFromSlides && (
                            <Button onClick={handleReset} size="small" type="link">
                                <Locale stringKey="YANDEX_STORY_FORM__COVER__RESET" />
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {hasUploadError && (
                <Alert
                    description={getLocalizedString('YANDEX_STORY_FORM__COVER__UPLOAD_ERROR')}
                    showIcon
                    type="error"
                />
            )}
        </div>
    );
}
